'use server'

import { redirect } from 'next/navigation'
import { z } from 'zod'

import { isPurchasable } from '@/features/catalog/lib/product-rules'
import { readDb } from '@/features/catalog/services/db.server'
import { SHOP_POLICY } from '@/features/site/config'

import {
  readCartItems,
  toCartView,
  writeCartItems,
} from '../services/cart.server'

import type { CartView } from '../schemas/cart.schema'

const addSchema = z.object({
  productId: z.string().min(1).max(20),
  optionId: z.string().max(20).nullable(),
  quantity: z.coerce.number().int().min(1).max(SHOP_POLICY.cartMaxQuantity),
  intent: z.enum(['cart', 'buy']),
})

export type AddToCartState = {
  status: 'idle' | 'error' | 'success'
  message?: string
  cartCount?: number
}

export async function addToCart(
  _prev: AddToCartState,
  formData: FormData
): Promise<AddToCartState> {
  const parsed = addSchema.safeParse({
    productId: formData.get('productId'),
    optionId: (formData.get('optionId') as string | null) || null,
    quantity: formData.get('quantity'),
    intent: formData.get('intent'),
  })
  if (!parsed.success)
    return { status: 'error', message: '상품 정보를 확인해 주세요.' }

  const { productId, optionId, quantity, intent } = parsed.data
  const db = await readDb()
  const product = db.products.find((item) => item.id === productId)
  if (!product || !isPurchasable(product)) {
    return { status: 'error', message: '현재 구매할 수 없는 상품입니다.' }
  }
  if (product.options.length > 0 && !optionId) {
    return { status: 'error', message: '옵션을 선택해 주세요.' }
  }
  if (optionId && !product.options.some((option) => option.id === optionId)) {
    return { status: 'error', message: '옵션을 다시 선택해 주세요.' }
  }

  const items = await readCartItems()
  const existing = items.find(
    (item) => item.p === productId && item.o === optionId
  )
  if (existing) {
    existing.q = Math.min(existing.q + quantity, SHOP_POLICY.cartMaxQuantity)
  } else {
    if (items.length >= SHOP_POLICY.cartMaxLines) {
      return {
        status: 'error',
        message: '장바구니에 담을 수 있는 상품 수를 초과했습니다.',
      }
    }
    items.push({ p: productId, o: optionId, q: quantity })
  }
  await writeCartItems(items)
  if (intent === 'buy') redirect('/checkout')
  return {
    status: 'success',
    message: '장바구니에 담았습니다.',
    cartCount: items.reduce((sum, item) => sum + item.q, 0),
  }
}

const lineSchema = z.object({
  productId: z.string().min(1).max(20),
  optionId: z.string().max(20).nullable(),
})

const quantitySchema = z.number().int().min(1).max(SHOP_POLICY.cartMaxQuantity)

export async function setCartQuantity(
  productId: string,
  optionId: string | null,
  quantity: number
): Promise<CartView | null> {
  const line = lineSchema.safeParse({ productId, optionId })
  const nextQuantity = quantitySchema.safeParse(quantity)
  if (!line.success || !nextQuantity.success) return null
  const items = await readCartItems()
  const target = items.find(
    (item) => item.p === line.data.productId && item.o === line.data.optionId
  )
  if (!target) return null
  target.q = nextQuantity.data
  await writeCartItems(items)
  return toCartView(await readDb(), items)
}

export async function removeCartLine(
  productId: string,
  optionId: string | null
): Promise<CartView | null> {
  const line = lineSchema.safeParse({ productId, optionId })
  if (!line.success) return null
  const items = (await readCartItems()).filter(
    (item) => !(item.p === line.data.productId && item.o === line.data.optionId)
  )
  await writeCartItems(items)
  return toCartView(await readDb(), items)
}
